import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import { useCompany } from '@/contexts/CompanyContext';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function PreferencesSettings() {
    const { showSmartTabs, setShowSmartTabs } = useAuth();
    const { theme, changeThemeWithTransition } = useTheme();
    const { currentTenant } = useTenant();
    const { currentCompany } = useCompany();
    const [language, setLanguage] = useState('English (US)');
    const [startWeek, setStartWeek] = useState(false);
    const [showViewHistory, setShowViewHistory] = useState(false);
    const [profileDiscoverability, setProfileDiscoverability] = useState(true);

    const activeOrgName = (currentTenant?.name && currentTenant?.name !== 'Primary Workspace' && currentTenant?.name !== 'Primary Organization') 
        ? currentTenant.name.replace(' (Fallback)', '') 
        : (currentCompany?.name || 'The Scale Agency');
    const activeOrgDomain = currentTenant?.domain || currentCompany?.domain || 'thescale.agency';

    const handleThemeChange = (newTheme) => {
        if (changeThemeWithTransition) {
            changeThemeWithTransition(newTheme);
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 8 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl pb-24"
        >
            {/* Page Header */}
            <div className="mb-6">
                <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 500, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>
                    Preferences
                </h3>
                <p style={{ fontSize: 'var(--text-md)', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Choose how you want Oblique to look and behave.
                </p>
            </div>

            {/* Active Workspace & Branding Card */}
            <div className="mb-8 p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center shrink-0">
                        <ObliqueIcon name="domain" size={22} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-[14px] font-bold text-[var(--text-primary)]">
                                {activeOrgName}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[var(--accent-subtle)] text-[var(--accent)]">
                                Active Workspace
                            </span>
                        </div>
                        <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
                            Domain: <span className="font-mono text-[var(--text-primary)] font-medium">{activeOrgDomain}</span> • Light & dark logos configured
                        </p>
                    </div>
                </div>
                <Link
                    to="/settings/admin/company"
                    className="h-8 px-3.5 rounded-lg bg-[var(--accent)] text-white hover:opacity-90 text-[12px] font-semibold flex items-center gap-1.5 transition-all no-underline shrink-0 shadow-xs"
                >
                    <ObliqueIcon name="edit" size={13} />
                    <span>Edit Company, Domain & Logo</span>
                </Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

                {/* ─── Appearance ─── */}
                <section>
                    <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: 'var(--text-lg)', fontWeight: 500, color: '#111' }}>Appearance</h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                            Customize the visual theme of the application.
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111' }}>Theme</label>
                        <Select value={theme} onValueChange={handleThemeChange}>
                            <SelectTrigger 
                                style={{ 
                                    width: '180px', height: '32px', 
                                    border: '0.5px solid rgba(0,0,0,0.15)', 
                                    borderRadius: 'var(--r-md)',
                                    fontSize: 'var(--text-sm)',
                                    background: 'white',
                                    padding: '0 10px',
                                }}
                            >
                                <SelectValue placeholder="Select a theme" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="light" style={{ fontSize: 'var(--text-sm)' }}>Light</SelectItem>
                                <SelectItem value="dark" style={{ fontSize: 'var(--text-sm)' }}>Dark</SelectItem>
                                <SelectItem value="system" style={{ fontSize: 'var(--text-sm)' }}>Use system setting</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)', margin: '4px 0' }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <div>
                            <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111', display: 'block' }}>Smart Tab Bar (Multi-Tab Workspace)</label>
                            <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                                Keep open module pages in the top navigation bar for quick switching.
                            </p>
                        </div>
                        <Switch checked={showSmartTabs} onCheckedChange={(val) => setShowSmartTabs(val)} />
                    </div>
                </section>

                <Separator style={{ background: 'rgba(0,0,0,0.06)', height: '0.5px', margin: '8px 0' }} />

                {/* ─── Language & time ─── */}
                <section style={{ paddingTop: '20px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: 'var(--text-lg)', fontWeight: 500, color: '#111' }}>Language & time</h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                            Set your locale and calendar preferences.
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111' }}>Language</label>
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger 
                                style={{ 
                                    width: '180px', height: '32px',
                                    border: '0.5px solid rgba(0,0,0,0.15)', 
                                    borderRadius: 'var(--r-md)',
                                    fontSize: 'var(--text-sm)',
                                    background: 'white',
                                    padding: '0 10px',
                                }}
                            >
                                <SelectValue placeholder="Select language" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="English (US)" style={{ fontSize: 'var(--text-sm)' }}>English (US)</SelectItem>
                                <SelectItem value="English (UK)" style={{ fontSize: 'var(--text-sm)' }}>English (UK)</SelectItem>
                                <SelectItem value="Spanish" style={{ fontSize: 'var(--text-sm)' }}>Español</SelectItem>
                                <SelectItem value="French" style={{ fontSize: 'var(--text-sm)' }}>Français</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)', margin: '4px 0' }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <div>
                            <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111', display: 'block' }}>Start week on Monday</label>
                            <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                                This will affect the way your calendars appear.
                            </p>
                        </div>
                        <Switch checked={startWeek} onCheckedChange={setStartWeek} />
                    </div>
                </section>

                <Separator style={{ background: 'rgba(0,0,0,0.06)', height: '0.5px', margin: '8px 0' }} />

                {/* ─── Privacy ─── */}
                <section style={{ paddingTop: '20px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: 'var(--text-lg)', fontWeight: 500, color: '#111' }}>Privacy</h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                            Control what information is visible to others.
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <div>
                            <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111', display: 'block' }}>Cookie settings</label>
                            <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                                Manage your cookie preferences.
                            </p>
                        </div>
                        <button 
                            style={{ 
                                fontSize: 'var(--text-sm)', fontWeight: 500, color: '#666',
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '4px 0',
                                transition: 'color 120ms ease',
                            }}
                            onMouseEnter={e => e.target.style.color = '#111'}
                            onMouseLeave={e => e.target.style.color = '#666'}
                        >
                            Customize
                        </button>
                    </div>

                    <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)', margin: '4px 0' }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <div>
                            <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111', display: 'block' }}>Show my view history</label>
                            <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                                People with edit and full access will see when you've viewed a page.
                            </p>
                        </div>
                        <Switch checked={showViewHistory} onCheckedChange={setShowViewHistory} />
                    </div>

                    <div style={{ height: '0.5px', background: 'rgba(0,0,0,0.06)', margin: '4px 0' }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                        <div>
                            <label style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: '#111', display: 'block' }}>Profile discoverability</label>
                            <p style={{ fontSize: 'var(--text-sm)', color: '#999', marginTop: '2px' }}>
                                Users who know your email will see your name when inviting you.
                            </p>
                        </div>
                        <Switch checked={profileDiscoverability} onCheckedChange={setProfileDiscoverability} />
                    </div>
                </section>
            </div>
        </motion.div>
    );
}
